import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { get } from '@/features/shared/lib/api';
import { arr } from '@/features/shared/lib/utils';
import { ChartSkeletonLoader } from '@/features/shared/ui/ChartSkeletonLoader';

function formatScholarName(row) {
    const eng = row?.scholar_name_eng || row?.scholar_name || '';
    const kor = row?.scholar_name_kor || '';
    if (eng && kor) return `${eng}(${kor})`;
    return eng || kor || '—';
}

export default function DepartmentTopResearcherCard({
    title,
    yearRangeLabel, // e.g. "(2024-2025)" — 타이틀 아래 작게 회색으로 표시
    deptId,
    startYear,
    endYear,
    endpoint, // '/rm/v1/department/top_cited' | '/rm/v1/department/top_publishing'
    size = 1,
}) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);

    const enabled = useMemo(() => !!deptId && startYear != null && endYear != null && !!endpoint, [deptId, startYear, endYear, endpoint]);

    useEffect(() => {
        if (!enabled) return;
        let alive = true;
        (async () => {
            setLoading(true);
            setErr(null);
            try {
                const data = await get(endpoint, {
                    params: {
                        dept_id: deptId,
                        start_year: startYear,
                        end_year: endYear,
                        size,
                    }
                });
                if (!alive) return;

                // WP 응답: { status, result } / 백엔드 원본: { records: [] }
                const list = arr(data?.result?.records || data?.records);
                setRows(list);
            } catch (e) {
                if (!alive) return;
                setErr(e?.response?.data?.message || e?.message || 'API error');
                setRows([]);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [enabled, deptId, startYear, endYear, endpoint, size]);

    return (
        <div className="chart-area p-4 border border-[#E4E8F1] rounded-xl mb-[20px] lg:w-[calc((100%-30px)/2)] lg:p-0 lg:mb-[60px] lg:border-none lg:rounded-0">
            <h3 className="mb-4 text-[15px] font-bold leading-[150%] text-[#6C7687] text-center lg:mb-6 lg:text-[18px] lg:text-[#333] lg:text-left">
                {title}
                {yearRangeLabel && (
                    <span className="block mt-1 text-[13px] font-normal text-[#8D9AAF] lg:text-[14px]">
                        {yearRangeLabel}
                    </span>
                )}
            </h3>

            <div className="p-5 border border-[#E4E8F1] rounded-[12px] bg-[#F3F5F9] xl:px-10 xl:py-9">
                {loading ? (
                    <div className="relative h-[220px]">
                        <ChartSkeletonLoader show={loading} />
                    </div>
                ) : err ? (
                    <div className="text-[14px] text-red-600 font-medium">
                        에러: {String(err)}
                    </div>
                ) : rows.length === 0 ? (
                    <div className="text-[14px] text-[#6C7687] font-medium">
                        데이터가 없습니다.
                    </div>
                ) : (
                    <div className="flex flex-col gap-4">
                        {rows.map((row, idx) => {
                            const scholarId = row?.scholar_id;
                            const name = formatScholarName(row);
                            const deptName = row?.dept_name || row?.dept_name_eng || row?.dept || '';
                            const cited = Number(row?.cited_count) || 0;
                            const pubs = Number(row?.publication_count) || 0;

                            const publicationsTo = scholarId
                                ? `/publications/?scholar_id=${encodeURIComponent(scholarId)}`
                                : null;

                            return (
                                <div key={scholarId || idx} className="slide relative flex-shrink-0 w-full">
                                    <div>
                                        <div className="absolute top-0 right-0 xl:top-[-12px] xl:right-[-10px]">
                                            <img src={rmVars.themeUrl + 'images/badge.png'} className="w-[37px]" alt="medal badge" />
                                        </div>

                                        <div className="xl:flex items-center gap-[15px]">
                                            <div className="w-[72px] h-[72px] overflow-hidden rounded-full border border-[#E4E8F1] mx-auto xl:mx-0 mb-[10px] xl:mb-0 xl:w-[65px] xl:h-[65px] xl:shrink-0">
                                                <img src={row?.profile_pic || (rmVars.themeUrl + 'images/profile.png')} alt="photo" />
                                            </div>

                                            <div className="text-center leading-[140%] xl:text-left xl:pr-7">
                                                <p className="mb-[5px] text-[15px] xl:text-[16px] text-[#376BDB] font-bold">
                                                    {scholarId ? (
                                                        <Link to={`/researchers/${scholarId}`} className="hover:underline underline-offset-2 decoration-1 text-[#376BDB] font-bold">
                                                            {name}
                                                        </Link>
                                                    ) : (
                                                        name
                                                    )}
                                                </p>
                                                {!!deptName && (
                                                    <p className="text-[14px] text-[#8D9AAF] leading-4">
                                                        {deptName}
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        <ul className="mt-[14px] px-[16px] border border-[#E4E8F1] rounded-[12px] text-[14px] text-[#6C7687] font-bold leading-[140%] bg-white xl:mt-4">
                                            <li className="flex justify-between items-center py-[10px] border-b border-b-[#E4E8F1] xl:py-[14px]">
                                                <span>Scholarly Output</span>
                                                {publicationsTo && pubs ? (
                                                    <Link to={publicationsTo} className="hover:underline underline-offset-2 decoration-1">
                                                        <span className="text-[#376BDB]">{pubs}</span>
                                                    </Link>
                                                ) : pubs ? (
                                                    <span className="text-[#333] text-[#376BDB]">{pubs}</span>
                                                ) : (
                                                    <span className="text-[#333]">—</span>
                                                )}
                                            </li>
                                            <li className="flex justify-between items-center py-[10px] xl:py-[14px]">
                                                <span>Citation Count</span>
                                                <span className="text-[#333]">{cited}</span>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

